import { useEffect, useState } from "react";
import { http } from "../api";
import { Megaphone, HandCoins } from "lucide-react";
import { Modal, PageHeader, AddButton, FormRow, EmptyState } from "../shared";

const empty = { title: "", description: "", starts_on: "", ends_on: "", ward: 7, target: 100, status: "Active" };
const donateEmpty = { amount: 500, donor_name: "", donor_mobile: "", donor_email: "" };

export default function Campaigns() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [donateFor, setDonateFor] = useState(null);
  const [donateForm, setDonateForm] = useState(donateEmpty);
  const [donateBusy, setDonateBusy] = useState(false);
  const [rzpStatus, setRzpStatus] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => http.get("/campaigns").then((r) => setRows(r.data));
  useEffect(() => { load(); http.get("/razorpay/status").then((r) => setRzpStatus(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try { await http.post("/campaigns", { ...form, ward: Number(form.ward), target: Number(form.target) }); setShow(false); setForm(empty); load(); }
    finally { setSaving(false); }
  };

  const loadRzpScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const donate = async () => {
    if (!donateForm.donor_name || !donateForm.amount) return;
    setDonateBusy(true); setMsg("");
    try {
      const r = await http.post("/razorpay/order", {
        ...donateForm, amount: Number(donateForm.amount), campaign_id: donateFor.campaign_id,
      });
      if (r.data.provider === "simulated") {
        // Simulated flow: post verify with fake payment id
        await http.post("/razorpay/verify", { razorpay_order_id: r.data.order_id, razorpay_payment_id: "pay_sim_" + Date.now(), razorpay_signature: "sim" });
        setMsg(`Simulated donation of ₹${donateForm.amount} recorded (Razorpay not configured — added to Finance/donations).`);
        setDonateFor(null); setDonateForm(donateEmpty);
        return;
      }
      // Live Razorpay checkout
      const ok = await loadRzpScript();
      if (!ok) { setMsg("Failed to load Razorpay checkout script"); return; }
      const options = {
        key: r.data.key_id, amount: r.data.amount, currency: r.data.currency,
        name: donateFor.title, description: "Donation to campaign",
        order_id: r.data.order_id,
        prefill: { name: donateForm.donor_name, contact: donateForm.donor_mobile, email: donateForm.donor_email },
        theme: { color: "#0F766E" },
        handler: async (resp) => {
          try {
            await http.post("/razorpay/verify", resp);
            setMsg("Payment verified & recorded. Thank you!");
            setDonateFor(null); setDonateForm(donateEmpty);
          } catch (e) { setMsg("Verification failed: " + (e.response?.data?.detail || e.message)); }
        },
        modal: { ondismiss: () => setDonateBusy(false) },
      };
      new window.Razorpay(options).open();
    } catch (e) {
      setMsg("Donation failed: " + (e.response?.data?.detail || e.message));
    } finally { setDonateBusy(false); }
  };

  return (
    <>
      <PageHeader eyebrow="OUTREACH & PROJECTS" title="Campaigns" subtitle={`${rows.length} initiatives running`}
        actions={<AddButton onClick={() => setShow(true)} label="Create campaign" testid="campaigns-add-button" />} />

      {msg && <div className="notice-strip" data-testid="donation-msg" style={{ background: "#e5f2e3", color: "#3a7647", borderColor: "#c1dbb9" }}><HandCoins size={16} />{msg}</div>}

      <section className="panel">
        {rows.length === 0 ? <EmptyState icon={Megaphone} title="No campaigns" hint="Create your first campaign or project." /> : (
          <div className="grid-cards">
            {rows.map((c, i) => {
              const pct = Math.min(100, Math.round(((c.progress || 0) / (c.target || 1)) * 100));
              return (
                <div key={c.campaign_id} className="campaign-card" data-testid={`campaign-card-${i}`}>
                  <div className="c-head"><Megaphone size={17} /><b>{c.title}</b><span className="chip green">{c.status}</span></div>
                  <p className="hint">{c.description}</p>
                  <div className="c-dates"><small>{c.starts_on} → {c.ends_on}</small><small>Ward {c.ward}</small></div>
                  <div className="progress-bar" data-testid={`campaign-progress-${i}`}>
                    <div className="progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="c-nums"><span><b>{c.progress || 0}</b> of {c.target}</span><span>{pct}%</span></div>
                  <button className="primary" style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
                    data-testid={`campaign-donate-${i}`} onClick={() => { setDonateFor(c); setDonateForm(donateEmpty); }}>
                    <HandCoins size={16} /> Donate
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {show && (
        <Modal title="Create campaign" onClose={() => setShow(false)} testid="campaign-modal">
          <FormRow label="Title"><input data-testid="form-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormRow>
          <FormRow label="Description"><input data-testid="form-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
          <div className="form-grid">
            <FormRow label="Start date"><input data-testid="form-start-input" type="date" value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} /></FormRow>
            <FormRow label="End date"><input data-testid="form-end-input" type="date" value={form.ends_on} onChange={(e) => setForm({ ...form, ends_on: e.target.value })} /></FormRow>
            <FormRow label="Target"><input data-testid="form-target-input" type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} /></FormRow>
            <FormRow label="Status"><select data-testid="form-status-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option>Active</option><option>Draft</option><option>Paused</option><option>Archived</option></select></FormRow>
          </div>
          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => setShow(false)}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !form.title} onClick={save}>{saving ? "Saving…" : "Create"}</button>
          </div>
        </Modal>
      )}

      {donateFor && (
        <Modal title={`Donate to ${donateFor.title}`} onClose={() => setDonateFor(null)} testid="donate-modal">
          {!rzpStatus?.configured && (
            <div className="notice-strip" style={{ marginBottom: 12 }}><HandCoins size={14} />
              <span>Razorpay not configured — donation will be recorded in simulation mode. Add <code>RAZORPAY_KEY_ID</code> and <code>RAZORPAY_KEY_SECRET</code> to <code>backend/.env</code> for live UPI/card checkout.</span>
            </div>
          )}
          <div className="form-grid">
            <FormRow label="Amount (₹)"><input data-testid="donate-amount" type="number" min={1} value={donateForm.amount} onChange={(e) => setDonateForm({ ...donateForm, amount: e.target.value })} /></FormRow>
            <FormRow label="Donor name"><input data-testid="donate-name" value={donateForm.donor_name} onChange={(e) => setDonateForm({ ...donateForm, donor_name: e.target.value })} /></FormRow>
            <FormRow label="Mobile"><input data-testid="donate-mobile" value={donateForm.donor_mobile} onChange={(e) => setDonateForm({ ...donateForm, donor_mobile: e.target.value })} /></FormRow>
            <FormRow label="Email"><input data-testid="donate-email" type="email" value={donateForm.donor_email} onChange={(e) => setDonateForm({ ...donateForm, donor_email: e.target.value })} /></FormRow>
          </div>
          <div className="form-actions">
            <button className="outline" data-testid="donate-cancel" onClick={() => setDonateFor(null)}>Cancel</button>
            <button className="primary" data-testid="donate-submit" disabled={donateBusy || !donateForm.donor_name || !Number(donateForm.amount)} onClick={donate}>{donateBusy ? "Opening…" : `Pay ₹${donateForm.amount}`}</button>
          </div>
        </Modal>
      )}
    </>
  );
}
